import React, { createContext, useState, ReactNode } from 'react';

interface User {
  userId?: number;
  username?: string;
  email?: string;
  role?: string;
  profilePictureUrl?: string;
  timezone?: string;
  loginType?: string;
  // True when the account has a local password hash on the server. Drives
  // UI decisions that should NOT depend on loginType — a social user who
  // adds a password stays loginType="google" forever but is effectively a
  // hybrid account, and the modals should treat them like a credentials user
  // (ask for Current Password, show Change Password instead of Set Password).
  hasPassword?: boolean;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthContextType {
  authState: AuthState;
  setAuthState: React.Dispatch<React.SetStateAction<AuthState>>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>(() => {
    // Initial state: try to load from localStorage
    const savedUser = localStorage.getItem('userData');
    if (savedUser) {
      try {
        const user = JSON.parse(savedUser);
        return {
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null
        };
      } catch (e) {
        console.error('Error parsing userData from localStorage', e);
      }
    }
    return {
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null
    };
  });

  return (
    <AuthContext.Provider value={{ authState, setAuthState }}>
      {children}
    </AuthContext.Provider>
  );
};
